import { useMemo, useRef, useState } from "react"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { CarouselProgress } from "~/components/CarouselProgress"
import {
  ComingNextCarousel,
  scrollComingNextCarouselTo,
} from "~/components/ComingNextCarousel"
import { ComingNextFeatureCard } from "~/components/ComingNextFeatureCard"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { UnlimitedAskDynamoCarouselCard } from "~/components/UnlimitedAskDynamoCarouselCard"
import { trackEvent } from "~/utils/trackEvent"
import { BookOpen, NotebookPen, Brain, Sparkles, PawPrint } from "lucide-react"

export function DeeperInsightsPlaceholderPage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const carouselRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    getProfile()
      .then((data) => setProfile(data))
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  const dogName = profile?.dog_profile?.name?.trim() || "your dog"

  const featureCards = useMemo(
    () => [
      {
        title: "Build a Personalised Guide",
        description: `Save helpful advice and build a personalised guide for ${dogName} over time.`,
        icon: BookOpen,
        items: ["Save useful advice", "Build over time", "Everything in one place"],
      },
      {
        title: "Save Tips & Notes",
        description:
          "Save Ask Dynamo answers and add your own observations to create a training journal.",
        icon: NotebookPen,
        items: [
          "Save advice you receive",
          "Add your own observations",
          "Build your own training knowledge",
        ],
      },
      {
        title: "Multiple Dogs",
        description: "Add all your dogs in one place.",
        icon: PawPrint,
        items: [
          "Separate profiles",
          "Individual personality types",
          "Personalised guidance for each",
        ],
      },
      {
        title: "Deeper Personality Insights",
        description: "Understand why behaviours happen and learn how to respond effectively.",
        icon: Brain,
        items: [
          "Why behaviours happen",
          "How to respond effectively",
          "Train with confidence",
        ],
      },
      {
        title: "Make the App Yours",
        description: `Upload photos and personalise ${dogName}'s experience.`,
        icon: Sparkles,
        items: ["Custom hero image", "Feels uniquely yours"],
      },
    ],
    [dogName],
  )

  const slideLabels = useMemo(
    () => ["Unlimited Ask Dynamo", ...featureCards.map((card) => card.title)],
    [featureCards],
  )

  const totalSlides = 1 + featureCards.length

  function handleCtaClick() {
    trackEvent("whats_coming_next_cta_tap", { dogName })
    scrollComingNextCarouselTo(carouselRef, 1)
    setActiveIndex(1)
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <header className="text-center">
          <h1 className="text-3xl font-semibold leading-tight text-[var(--color-text-primary)]">
            See What&apos;s Coming Next for {dogName}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            Discover new ways to understand, train and support {dogName}.
          </p>
        </header>

        <ComingNextCarousel
          carouselRef={carouselRef}
          slideCount={totalSlides}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
        >
          <UnlimitedAskDynamoCarouselCard dogName={dogName} />
          {featureCards.map((card) => (
            <ComingNextFeatureCard
              key={card.title}
              title={card.title}
              description={card.description}
              icon={card.icon}
              items={card.items}
            />
          ))}
        </ComingNextCarousel>

        <CarouselProgress
          total={totalSlides}
          activeIndex={activeIndex}
          labels={slideLabels}
        />

        <div className="mt-5 text-center">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Give {dogName} the best experience possible.
          </p>
          <button
            type="button"
            onClick={handleCtaClick}
            className="mt-3 rounded-lg bg-[var(--color-ask-dynamo)] px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
          >
            See What&apos;s Coming Next
          </button>
        </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
